import { COMMON } from './common'
import { TERMS } from './terms'

/**
 * The axes a product group describes its products by, the values a product
 * and a measure carry on them, and the links the two suggest.
 *
 * `NUMBER`, `SELECT` and `BOOLEAN` keep their English names in the type
 * picker's values but are read out in German — an operator picks "Zahl", the
 * column holds the member.
 */
export const PRODUCT_PROPERTY = {
  title: TERMS.productPropertyPlural,
  singular: TERMS.productProperty,
  /** The screen's own line under the heading. */
  intro: 'Einmal angelegt, von jeder Produktgruppe nutzbar.',
  empty: 'Noch keine Eigenschaft angelegt',
  add: 'Eigenschaft anlegen',
  key: 'Schlüssel',
  keyHint: 'camelCase, app-weit eindeutig. Danach nicht mehr änderbar.',
  type: COMMON.type,
  typeHint: 'Steht fest, sobald ein Wert erfasst ist.',
  usedBy: (count: number) =>
    count === 1 ? 'Von 1 Stelle genutzt' : `Von ${count} Stellen genutzt`,

  /** The properties section inside a product group. */
  group: {
    title: TERMS.characteristicPlural,
    hint: `Die Eigenschaften, die ein Produkt dieser ${TERMS.productGroup} hat.`,
    empty: 'Diese Gruppe hat noch keine Eigenschaften',
    edit: 'Eigenschaft bearbeiten',
    editHint:
      'Leer gelassen gilt, was die Eigenschaft selbst sagt — der Platzhalter zeigt es.',
    boundsLabel: 'Grenzen dieser Gruppe',
    iconHint: 'Ohne Auswahl gilt das Icon der Eigenschaft.',
    descriptionHint: 'Ohne Text gilt der Hilfetext der Eigenschaft.',
    isRequiredHint:
      'Ein Produkt dieser Gruppe sollte hier einen Wert haben. Fehlt er beim Import, wird er als Lücke vermerkt.',
    allAttached: 'Alle Eigenschaften sind dieser Gruppe schon zugeordnet.',
    narrowed: 'Angepasst',
    /** No axis exists app-wide yet, so there is nothing to attach anywhere. */
    noneDefined: 'Es gibt noch keine Eigenschaft, die zugeordnet werden kann.',
    manage: 'Eigenschaften anlegen',
    attach: 'Eigenschaft zuordnen',
    detach: 'Zuordnung entfernen',
    /** The other direction: a measure picks the groups it is carried out with,
     *  and those bring the axes. */
    groupsTitle: `${TERMS.productGroupPlural} zuordnen`,
    attachGroup: `${TERMS.productGroup} hinzufügen`,
    searchGroup: `${TERMS.productGroup} suchen …`,
    detachGroupConfirm: `Die Werte der ${TERMS.measure} auf den Eigenschaften dieser ${TERMS.productGroup} fallen damit weg.`,
    groupsHint: `Die Eigenschaften der zugeordneten ${TERMS.productGroupPlural} bilden das Datenblatt der ${TERMS.measure}. Entfernst du eine, fallen ihre Werte weg.`,
    noGroupsYet: 'Keine zugeordnet',
    isRequired: 'Pflichtfeld',
    offeredOptionsHint: 'Keine Auswahl heißt: alle.',
  },

  /** The datasheet on a product, and the one a measure prints. */
  values: {
    title: TERMS.characteristicPlural,
    /** The heading over either of the two lines below. */
    noGroupTitle: `Noch keine ${TERMS.productGroup} zugeordnet`,
    /** On a product, which is a product. */
    noGroup: `Erst eine ${TERMS.productGroup} zuordnen — sie bestimmt, welche Eigenschaften das Produkt hat.`,
    /** On a measure, whose groups also decide which products the matcher
     *  offers to link. */
    noGroupMeasure: `Erst eine ${TERMS.productGroup} zuordnen — sie bestimmt die Eigenschaften der ${TERMS.measure} und welche ${TERMS.productPlural} für sie vorgeschlagen werden.`,
    empty: `Diese ${TERMS.productGroup} hat noch keine Eigenschaften.`,
    foreign: 'Gehört nicht zu dieser Gruppe',
    foreignHint:
      'Der Wert stammt aus einer anderen Gruppe und bleibt erhalten, falls das Produkt zurückwechselt.',
    removeForeign: 'Wert entfernen',
    value: 'Wert',
    /** On a measure, whose sheet is read and never matched against. */
    measureHint: `Was auf dem Datenblatt der ${TERMS.measure} steht. Welche ${TERMS.productPlural} verknüpft werden dürfen, steht unter ${TERMS.productPlural}.`,
    rangeHint: 'Zwei Werte ergeben eine Spanne, ein Wert eine genaue Angabe.',
    optionsHint: 'Mehrere Möglichkeiten werden nebeneinander angezeigt.',
    editSpec: 'Wert bearbeiten',
    clearValue: 'Wert entfernen',
  },

  /**
   * The matrix that decides which products a measure may be carried out with.
   *
   * One row per way a product may qualify, one column per characteristic. The
   * rows are alternatives and the cells of one row hold together, which is the
   * whole reason the screen is a table: a measure takes 4–6 mm glued or 10–14 mm
   * clicked, and nothing in between.
   */
  requirements: {
    title: 'Anforderungen',
    hint: `Eine Zeile je Variante, eine Spalte je ${TERMS.characteristic}. Ein ${TERMS.product} passt, wenn es eine Zeile vollständig erfüllt.`,
    empty: `Noch keine Anforderung — jedes ${TERMS.product} der zugeordneten ${TERMS.productGroupPlural} passt.`,
    /** The noun a row is, read by its column head and by the button that adds
     *  one. */
    variant: 'Variante',
    /** What a row is called while nobody has named it. */
    variantNumber: (position: number) => `Variante ${position}`,
    /** A cell nothing is stated in. */
    any: 'beliebig',
    matches: 'Treffer',
    matchesHint: `${TERMS.productPlural}, die diese Zeile erfüllen — Datenblatt und Preis. Bestehende Verknüpfungen prüft die Liste darunter.`,
    openVariant: 'Ohne Bedingung — diese Zeile trifft auf alles zu.',
    editCell: (characteristic: string) => `${characteristic} einschränken`,
    clearCell: 'Bedingung entfernen',
    /** The price column of the matrix: what a product may cost to meet this
     *  one row. Only ADMIN and ROOT write it. */
    editPrice: 'Preis einschränken',
    clearPrice: 'Preisgrenze entfernen',
    priceReadOnly: 'Die Preisgrenze setzt die Administration.',
    removeVariant: 'Variante entfernen',
    removeVariantConfirm: 'Die Bedingungen dieser Variante fallen damit weg.',
    /** Under the matrix: what the rows come to together. */
    total: (count: number) =>
      count === 1
        ? `1 ${TERMS.product} erfüllt mindestens eine Variante.`
        : `${count} ${TERMS.productPlural} erfüllen mindestens eine Variante.`,
  },

  /** What a measure pays for a product on one row of the matrix, per the
   *  measure's own unit. */
  price: {
    bounds: 'Produktpreis',
    hint: 'Netto je Einheit. Ein Produkt außerhalb dieser Spanne erfüllt diese Zeile nicht.',
    noUnit: 'Erst eine Einheit an der Maßnahme wählen.',
  },

  /**
   * The operator's repair for the matcher, under System → Betrieb.
   *
   * It re-reads itself after every write the application makes, so this is
   * for the changes it did not make: eine Migration, ein Restore, ein Eingriff
   * direkt in die Datenbank.
   */
  pass: {
    title: 'Produktvorschläge neu bewerten',
    subtitle:
      'Nach jeder Änderung im Katalog wird automatisch neu bewertet. Diese Aktion ist für Änderungen, die nicht über die Anwendung liefen — eine Migration, ein Restore, ein direkter Eingriff in die Datenbank.',
    scope: (measures: number, products: number) =>
      `${measures} ${measures === 1 ? TERMS.measure : TERMS.measurePlural} gegen ${products} ${products === 1 ? TERMS.product : TERMS.productPlural}`,
    open: (count: number) =>
      count === 1
        ? '1 offener Vorschlag steht aktuell'
        : `${count} offene Vorschläge stehen aktuell`,
    start: 'Neu bewerten',
    loadError:
      'Der Status konnte nicht geladen werden. Ob gerade ein Durchlauf läuft, ist unbekannt.',
    runningHint:
      'Der Durchlauf läuft im Hintergrund. Die Vorschläge stehen, sobald er durch ist.',
    kept: 'Abgelehnte Vorschläge bleiben abgelehnt, verknüpfte Produkte bleiben verknüpft.',
  },

  /** The suggestions the matrix above collects, and what becomes of them. */
  suggestions: {
    title: 'Vorgeschlagene Produkte',
    hint: 'Produkte, die alle Anforderungen erfüllen und im Preis liegen.',
    empty: 'Zurzeit passt nichts Neues',
    confirm: 'Verknüpfen',
    reject: 'Ablehnen',
    rejectedHint:
      'Kommt von selbst zurück, sobald sich am Produkt etwas ändert, das die Prüfung liest.',
    linkAnyway: 'Doch verknüpfen',
    notification: (count: number, measure: string) =>
      count === 1
        ? `Ein Produkt passt jetzt zu „${measure}”.`
        : `${count} Produkte passen jetzt zu „${measure}”.`,
  },

  /** The free-text datasheet a measure still carries, while it is moved over. */
  legacy: {
    title: 'Alte Technische Daten',
    hint: 'Jede Zeile wird einzeln auf eine Eigenschaft der Produktgruppen dieser Maßnahme übertragen. Was übertragen ist, bleibt hier stehen und ist markiert.',
    migrate: 'Übertragen',
    deleteConfirm:
      'Die Angabe ist danach weg. Übertragen statt löschen, falls sie noch gebraucht wird.',
    migrated: 'Übertragen',
    migrateTitle: (key: string) => `„${key}” übertragen`,
    needsValue: 'Erst den Wert eintragen, der auf die Eigenschaft gehört.',
    axisTaken: 'Diese Eigenschaft hat an dieser Maßnahme schon einen Wert.',
    migrateHint:
      'Auf welche Eigenschaft die Angabe gehört und welcher Wert das dort ist. Der Wert wird neu erfasst, weil erst die Eigenschaft die Einheit und die Auswahlmöglichkeiten vorgibt.',
  },

  errors: {
    keyTaken: (key: string) => `Eine Eigenschaft „${key}” gibt es bereits.`,
    stillUsed: (count: number) =>
      count === 1
        ? 'Wird noch an einer Stelle gelesen. Dort zuerst entfernen.'
        : `Wird noch an ${count} Stellen gelesen. Dort zuerst entfernen.`,
    optionStillUsed: (count: number) =>
      count === 1
        ? 'Eine Stelle nutzt diese Möglichkeit noch. Dort zuerst entfernen.'
        : `${count} Stellen nutzen diese Möglichkeit noch. Dort zuerst entfernen.`,
    wrongType: (label: string) => `„${label}” nimmt diese Art von Wert nicht.`,
    belowMinimum: (label: string, min: number) =>
      `„${label}” muss mindestens ${min} sein.`,
    aboveMaximum: (label: string, max: number) =>
      `„${label}” darf höchstens ${max} sein.`,
    optionNotOffered: (label: string) =>
      `Diese Möglichkeit bietet die Gruppe für „${label}” nicht an.`,
    optionUnknown: (label: string) =>
      `„${label}” hat diese Möglichkeit nicht. Lade die Seite neu.`,
    alreadyAttached: 'Diese Eigenschaft ist der Gruppe schon zugeordnet.',
    offersNothing: (label: string) =>
      `Die Gruppe muss mindestens eine Möglichkeit von „${label}” anbieten. Sonst die Eigenschaft von der Gruppe lösen.`,
    notInGroups: `Diese Eigenschaft gehört zu keiner zugeordneten ${TERMS.productGroup}.`,
    variantNotFound:
      'Diese Variante gehört nicht zu dieser Maßnahme. Lade die Seite neu.',
    priceNeedsUnit:
      'Für eine Preisgrenze braucht die Maßnahme eine Einheit — sonst lässt sich kein Produktpreis damit vergleichen.',
    suggestionDecided: 'Über diesen Vorschlag ist bereits entschieden.',
    decisionUnknown: 'Diese Entscheidung kennt der Server nicht.',
  },
} as const
